/**
 * Node Settings Types
 *
 * Based on LogicAppsUX patterns for Standard SKU workflows.
 * Each setting uses SettingData<T> wrapper with isSupported flag
 * to enable dynamic availability based on operation manifest.
 */

// =============================================================================
// CORE SETTING WRAPPER
// =============================================================================

/**
 * Generic wrapper for each setting that indicates support and holds value.
 * This pattern allows UI to conditionally render settings based on operation type.
 */
export interface SettingData<T> {
  /** Whether this setting is supported for the current operation */
  isSupported: boolean;
  /** The actual value of the setting */
  value?: T;
  /** Validation error message if any */
  validationError?: string;
}

// =============================================================================
// SETTING VALUE TYPES
// =============================================================================

/**
 * Simple setting with enable toggle and optional value
 */
export interface SimpleSetting<T> {
  enabled: boolean;
  value?: T;
}

/**
 * Retry policy configuration
 * Supports 4 types: default, none, fixed, exponential
 */
export interface RetryPolicy {
  /** Type of retry policy */
  type: RetryPolicyType;
  /** Number of retry attempts (1-90) */
  count?: number;
  /** Interval between retries (ISO 8601 duration, e.g., "PT5S") */
  interval?: string;
  /** Minimum interval for exponential backoff */
  minimumInterval?: string;
  /** Maximum interval for exponential backoff */
  maximumInterval?: string;
}

export type RetryPolicyType = 'default' | 'none' | 'fixed' | 'exponential';

/**
 * Concurrency control settings
 */
export interface ConcurrencySettings {
  /** Whether concurrency limiting is enabled */
  enabled: boolean;
  /** Degree of parallelism (1-100 for actions, 1-50 for triggers) */
  runs: number;
  /** Maximum waiting runs in queue (triggers only) */
  maximumWaitingRuns?: number;
}

/**
 * Run after configuration - defines predecessor dependencies
 */
export interface RunAfterSettings {
  /** Map of action name to status conditions that trigger this action */
  [actionName: string]: RunAfterStatus[];
}

export type RunAfterStatus = 'Succeeded' | 'Failed' | 'Skipped' | 'TimedOut';

/**
 * Correlation settings for trigger correlation
 */
export interface CorrelationSettings {
  /** Client tracking ID expression */
  clientTrackingId?: string;
}

/**
 * Split-on configuration for array-based instance splitting
 */
export interface SplitOnConfiguration {
  /** Expression to split on */
  expression?: string;
  /** Correlation settings for split instances */
  correlation?: CorrelationSettings;
}

/**
 * Request options (timeout for single request)
 */
export interface RequestOptions {
  /** Single request timeout (ISO 8601 duration) */
  timeout?: string;
}

/**
 * Upload chunk configuration
 */
export interface UploadChunk {
  /** Whether chunked upload is enabled */
  transferMode?: 'chunked';
  /** Chunk size in MB */
  uploadChunkSize?: number;
}

/**
 * Paging configuration
 */
export interface PagingSettings {
  /** Whether paging is enabled */
  enabled: boolean;
  /** Maximum number of items to retrieve */
  value?: number;
}

// =============================================================================
// MAIN SETTINGS INTERFACE
// =============================================================================

/**
 * Complete settings for a node/operation.
 * Each property uses SettingData wrapper for dynamic availability.
 *
 * Settings are organized by category:
 * - General: timeout, concurrency, splitOn, count
 * - Networking: retryPolicy, requestOptions, paging, chunking, async patterns
 * - Security: secureInputs, secureOutputs
 * - Tracking: trackedProperties, correlation
 * - Run After: runAfter dependencies
 * - Data Handling: schema validation, decompression
 */
export interface NodeSettings {
  // ==========================================================================
  // GENERAL SETTINGS
  // ==========================================================================

  /** Action/trigger timeout (ISO 8601 duration, e.g., "PT1H") */
  timeout?: SettingData<string>;

  /** Concurrency control - limit parallel executions */
  concurrency?: SettingData<ConcurrencySettings>;

  /** Split-on for array-based instance splitting (triggers) */
  splitOn?: SettingData<SimpleSetting<string>>;

  /** Allowed splitOn expressions derived from operation manifest outputs */
  splitOnAllowedValues?: string[] | undefined;

  /** Split-on correlation configuration */
  splitOnConfiguration?: SplitOnConfiguration;

  /** Maximum iteration count for loops */
  count?: SettingData<string | number>;

  /** Whether to fail when count/timeout limits are reached */
  shouldFailOperation?: SettingData<boolean>;

  /** Trigger conditions - expressions that must be true to fire */
  conditionExpressions?: SettingData<string[]>;

  // ==========================================================================
  // NETWORKING SETTINGS
  // ==========================================================================

  /** Retry policy configuration */
  retryPolicy?: SettingData<RetryPolicy>;

  /** Single request timeout */
  requestOptions?: SettingData<RequestOptions>;

  /** Enable async pattern (polling for long-running operations) */
  asynchronous?: SettingData<boolean>;

  /** Disable async pattern override */
  disableAsyncPattern?: SettingData<boolean>;

  /** Pagination settings */
  paging?: SettingData<PagingSettings>;

  /** Chunked upload configuration */
  uploadChunk?: SettingData<UploadChunk>;

  /** Download chunk size in MB */
  downloadChunkSize?: SettingData<number>;

  /** Suppress workflow headers in outgoing requests */
  suppressWorkflowHeaders?: SettingData<boolean>;

  /** Suppress workflow headers in responses */
  suppressWorkflowHeadersOnResponse?: SettingData<boolean>;

  // ==========================================================================
  // SECURITY SETTINGS
  // ==========================================================================

  /** Hide input values in run history */
  secureInputs?: SettingData<boolean>;

  /** Hide output values in run history */
  secureOutputs?: SettingData<boolean>;

  // ==========================================================================
  // TRACKING SETTINGS
  // ==========================================================================

  /** Custom tracked properties for monitoring (key-value pairs) */
  trackedProperties?: SettingData<Record<string, string>>;

  /** Correlation settings */
  correlation?: SettingData<CorrelationSettings>;

  // ==========================================================================
  // RUN AFTER SETTINGS
  // ==========================================================================

  /** Run after configuration - predecessor dependencies */
  runAfter?: SettingData<RunAfterSettings>;

  // ==========================================================================
  // DATA HANDLING SETTINGS
  // ==========================================================================

  /** Validate request body against schema (HTTP triggers) */
  requestSchemaValidation?: SettingData<boolean>;

  /** Disable automatic decompression of responses */
  disableAutomaticDecompression?: SettingData<boolean>;

  // ==========================================================================
  // CONNECTION SETTINGS (Standard SKU specific)
  // ==========================================================================

  /** Use invoker's connection for Dataverse triggers */
  invokerConnection?: SettingData<SimpleSetting<boolean>>;
}

// =============================================================================
// SETTINGS SECTION NAMES (for UI organization)
// =============================================================================

export const SettingSectionName = {
  GENERAL: 'general',
  NETWORKING: 'networking',
  SECURITY: 'security',
  TRACKING: 'tracking',
  RUNAFTER: 'runafter',
  DATAHANDLING: 'datahandling',
} as const;

export type SettingSectionName = (typeof SettingSectionName)[keyof typeof SettingSectionName];

// =============================================================================
// SETTINGS UI STATE
// =============================================================================

/**
 * UI state for settings panel
 */
export interface SettingsUIState {
  /** Which sections are currently expanded */
  expandedSections: SettingSectionName[];
  /** Validation errors per field */
  validationErrors: Record<string, string>;
}

// =============================================================================
// SETTINGS SCOPE (for manifest-based availability)
// =============================================================================

export const SettingScope = {
  Trigger: 'trigger',
  Action: 'action',
} as const;

export type SettingScope = (typeof SettingScope)[keyof typeof SettingScope];

/**
 * Setting definition from operation manifest
 */
export interface OperationManifestSetting<T = void> {
  /** Which scopes this setting applies to (undefined = both) */
  scopes?: SettingScope[];
  /** Setting-specific options */
  options?: T;
}

// =============================================================================
// VALIDATION
// =============================================================================

export const SettingsValidationErrorCode = {
  TIMEOUT_INVALID: 'TIMEOUT_INVALID',
  RETRY_COUNT_INVALID: 'RETRY_COUNT_INVALID',
  RETRY_INTERVAL_INVALID: 'RETRY_INTERVAL_INVALID',
  CONCURRENCY_RUNS_INVALID: 'CONCURRENCY_RUNS_INVALID',
  PAGING_COUNT_INVALID: 'PAGING_COUNT_INVALID',
  CHUNK_SIZE_INVALID: 'CHUNK_SIZE_INVALID',
  CIRCULAR_DEPENDENCY: 'CIRCULAR_DEPENDENCY',
  TRIGGER_CONDITION_EMPTY: 'TRIGGER_CONDITION_EMPTY',
} as const;

export type SettingsValidationErrorCode =
  (typeof SettingsValidationErrorCode)[keyof typeof SettingsValidationErrorCode];

/**
 * Validation error with details
 */
export interface SettingsValidationError {
  code: SettingsValidationErrorCode;
  message: string;
  field: string;
  section: SettingSectionName;
}

// =============================================================================
// HELPER FUNCTIONS
// =============================================================================

/**
 * Create a SettingData wrapper with isSupported = true
 */
export function createSupportedSetting<T>(value: T): SettingData<T>;
export function createSupportedSetting<T>(): SettingData<T>;
export function createSupportedSetting<T>(value?: T): SettingData<T> {
  if (value === undefined) {
    return { isSupported: true };
  }
  return { isSupported: true, value };
}

/**
 * Create a SettingData wrapper with isSupported = false
 */
export function createUnsupportedSetting<T>(): SettingData<T> {
  return { isSupported: false };
}

/**
 * Check if a setting is supported and has a value
 */
export function hasSettingValue<T>(setting: SettingData<T> | undefined): setting is SettingData<T> & { value: T } {
  return !!setting?.isSupported && setting.value !== undefined;
}

/**
 * Default retry policy values
 */
export const DEFAULT_RETRY_POLICY: RetryPolicy = {
  type: 'default',
  count: 4,
  interval: 'PT7S',
  minimumInterval: 'PT5S',
  maximumInterval: 'PT1H',
};

/**
 * Backfill the required fields for a retry policy based on its type.
 *
 * The Logic Apps runtime rejects 'Fixed'/'Exponential' policies that are
 * missing 'Count'/'Interval' (and 'Min/MaxInterval' for exponential). This
 * helper guarantees those required fields are present by drawing missing
 * values from {@link DEFAULT_RETRY_POLICY}, while preserving any explicit
 * values the caller already provided.
 *
 * Used both when reading an existing policy out of a workflow (heals stale
 * data saved by older versions) and when switching policy types in the UI.
 */
export function completeRetryPolicy(policy: RetryPolicy): RetryPolicy {
  const { type } = policy;
  const next: RetryPolicy = { type };
  if (type === 'fixed' || type === 'exponential') {
    const count = policy.count ?? DEFAULT_RETRY_POLICY.count;
    if (count !== undefined) next.count = count;
    const interval = policy.interval || DEFAULT_RETRY_POLICY.interval;
    if (interval) next.interval = interval;
  }
  if (type === 'exponential') {
    const minInterval = policy.minimumInterval || DEFAULT_RETRY_POLICY.minimumInterval;
    if (minInterval) next.minimumInterval = minInterval;
    const maxInterval = policy.maximumInterval || DEFAULT_RETRY_POLICY.maximumInterval;
    if (maxInterval) next.maximumInterval = maxInterval;
  }
  return next;
}

/**
 * Serialize a retry policy to its workflow JSON shape, or return `null` to
 * indicate the policy should be omitted entirely (`type: 'default'` is a
 * UI-only sentinel meaning "use the runtime's built-in default" — emitting
 * it would fail validation since `'default'` is not a valid `RetryType`).
 *
 * Only the fields relevant to the selected type are emitted, so switching
 * policies (e.g. exponential → none) never leaks stale count/interval values.
 */
export function serializeRetryPolicyForInputs(
  policy: RetryPolicy
): Record<string, unknown> | null {
  if (policy.type === 'default') return null;
  const out: Record<string, unknown> = { type: policy.type };
  if (policy.type === 'fixed' || policy.type === 'exponential') {
    if (policy.count !== undefined) out.count = policy.count;
    if (policy.interval) out.interval = policy.interval;
  }
  if (policy.type === 'exponential') {
    if (policy.minimumInterval) out.minimumInterval = policy.minimumInterval;
    if (policy.maximumInterval) out.maximumInterval = policy.maximumInterval;
  }
  return out;
}

/**
 * Default concurrency settings
 */
export const DEFAULT_CONCURRENCY: ConcurrencySettings = {
  enabled: false,
  runs: 25,
};
